import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Camera,
  ImagePlus,
  Loader2,
  Trash2,
  X,
  PawPrint,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_ALBUM_PHOTO_MB = 8;
const MAX_ALBUM_SIZE = 60;

export default function PetProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // photo URL
  const [confirmDelete, setConfirmDelete] = useState(null); // photo URL
  const fileInputRef = useRef(null);

  const loadPet = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pets/${id}`);
      setPet(res.data);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'Pet not found' : 'Failed to load pet');
      setPet(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadPet(); }, [loadPet]);

  const isOwner = !!(pet && user && pet.owner_id === user.id);
  const gallery = pet?.gallery_images || [];
  const heroPhoto = pet?.image_url || gallery[0] || null;

  // Multi-file upload → append to gallery_images
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (gallery.length + files.length > MAX_ALBUM_SIZE) {
      toast.error(`Album is capped at ${MAX_ALBUM_SIZE} photos. Delete some first.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of files) {
        if (file.size > MAX_ALBUM_PHOTO_MB * 1024 * 1024) {
          toast.error(`${file.name} too large (max ${MAX_ALBUM_PHOTO_MB}MB) — skipped`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/uploads/pet-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push(`${BACKEND}${response.data.url}`);
      }
      if (uploaded.length) {
        const nextGallery = [...gallery, ...uploaded];
        await axios.put(`${API}/pets/${id}`, { gallery_images: nextGallery });
        setPet(prev => ({ ...prev, gallery_images: nextGallery }));
        toast.success(uploaded.length === 1 ? 'Photo added to album' : `${uploaded.length} photos added`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (url) => {
    setConfirmDelete(null);
    try {
      const nextGallery = gallery.filter(u => u !== url);
      await axios.put(`${API}/pets/${id}`, { gallery_images: nextGallery });
      setPet(prev => ({ ...prev, gallery_images: nextGallery }));
      toast.success('Photo removed');
      if (lightbox === url) setLightbox(null);
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  const handleSetAsCover = async (url) => {
    try {
      // Move current cover into the gallery, and promote clicked photo to cover.
      const previousCover = pet.image_url;
      const nextGallery = gallery.filter(u => u !== url);
      if (previousCover && previousCover !== url) nextGallery.unshift(previousCover);
      await axios.put(`${API}/pets/${id}`, {
        image_url: url,
        gallery_images: nextGallery,
      });
      setPet(prev => ({ ...prev, image_url: url, gallery_images: nextGallery }));
      toast.success('Cover photo updated');
    } catch {
      toast.error('Failed to update cover');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-5xl space-y-6" data-testid="pet-profile-loading">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 w-full" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="aspect-square" />)}
        </div>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="container mx-auto px-4 py-16 text-center" data-testid="pet-profile-empty">
        <PawPrint className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-2xl font-medium mb-2">Pet not found</h1>
        <p className="text-muted-foreground mb-4">This pet profile isn&apos;t available.</p>
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="pet-profile-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Go back
        </Button>
      </div>
    );
  }

  const petAge = pet.age_years != null
    ? `${pet.age_years}y${pet.age_months ? ` ${pet.age_months}m` : ''}`
    : null;

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl" data-testid={`pet-profile-${pet.id}`}>
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate(-1)}
        data-testid="pet-profile-back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Button>

      {/* Hero card */}
      <Card className="overflow-hidden mb-6" data-testid="pet-profile-hero">
        <div className="grid md:grid-cols-[280px,1fr] gap-0">
          <div className="relative bg-amber-50 aspect-square md:aspect-auto md:min-h-[280px]">
            {heroPhoto ? (
              <img
                src={heroPhoto}
                alt={pet.name}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => setLightbox(heroPhoto)}
                data-testid="pet-profile-hero-photo"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-amber-700">
                <PawPrint className="w-16 h-16" />
              </div>
            )}
          </div>
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h1 className="text-3xl font-heading font-medium" data-testid="pet-profile-name">
                  {pet.name}
                </h1>
                <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                  <span className="capitalize">{pet.custom_pet_type || pet.pet_type}</span>
                  {pet.breed && <><span>·</span><span>{pet.breed}</span></>}
                  {petAge && <><span>·</span><span>{petAge}</span></>}
                  {pet.gender && <><span>·</span><span className="capitalize">{pet.gender}</span></>}
                </div>
                {pet.owner_username && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Owned by{' '}
                    <button
                      onClick={() => navigate(`/profile/${pet.owner_username}`)}
                      className="text-primary hover:underline"
                      data-testid="pet-profile-owner-link"
                    >
                      @{pet.owner_username}
                    </button>
                  </div>
                )}
              </div>
              {gallery.length > 0 && (
                <Badge variant="outline" className="whitespace-nowrap">
                  <Camera className="w-3 h-3 mr-1" /> {gallery.length} {gallery.length === 1 ? 'photo' : 'photos'}
                </Badge>
              )}
            </div>
            {pet.bio && (
              <p className="text-sm mt-4 whitespace-pre-wrap" data-testid="pet-profile-bio">
                {pet.bio}
              </p>
            )}
          </CardContent>
        </div>
      </Card>

      {/* Album section */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-heading font-medium flex items-center gap-2">
          <Camera className="w-5 h-5 text-amber-700" /> Photo album
        </h2>
        {isOwner && (
          <div>
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              className="hidden"
              onChange={handleUpload}
              id="pet-album-upload"
            />
            <label
              htmlFor="pet-album-upload"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border cursor-pointer transition-colors ${uploading ? 'bg-muted' : 'bg-amber-600 text-white hover:bg-amber-700 border-amber-700'}`}
              data-testid="pet-album-upload-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
              ) : (
                <><ImagePlus className="w-4 h-4" /> Add photos</>
              )}
            </label>
          </div>
        )}
      </div>

      {gallery.length === 0 ? (
        <Card className="border-dashed" data-testid="pet-album-empty">
          <CardContent className="p-10 text-center text-muted-foreground">
            <Camera className="w-10 h-10 mx-auto mb-3 opacity-60" />
            <p className="text-sm">
              {isOwner
                ? 'No photos yet — click "Add photos" to start your pet\'s picture book.'
                : `${pet.name} hasn\'t added any album photos yet.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
          data-testid="pet-album-grid"
        >
          {gallery.map((url, idx) => (
            <div
              key={url}
              className="group relative aspect-square rounded-lg overflow-hidden bg-muted"
              data-testid={`pet-album-photo-${idx}`}
            >
              <img
                src={url}
                alt={`${pet.name} ${idx + 1}`}
                className="w-full h-full object-cover cursor-zoom-in transition-transform duration-300 group-hover:scale-105"
                onClick={() => setLightbox(url)}
                loading="lazy"
              />
              {isOwner && (
                <div className="absolute inset-x-0 bottom-0 p-1.5 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleSetAsCover(url); }}
                    className="text-[11px] text-white bg-black/50 hover:bg-black/70 px-2 py-1 rounded"
                    data-testid={`pet-album-set-cover-${idx}`}
                  >
                    Set as cover
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(url); }}
                    className="p-1 rounded bg-red-600/80 hover:bg-red-600 text-white"
                    aria-label="Delete photo"
                    data-testid={`pet-album-delete-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black">
          <DialogHeader className="sr-only">
            <DialogTitle>{pet.name} photo</DialogTitle>
          </DialogHeader>
          {lightbox && (
            <div className="relative">
              <img
                src={lightbox}
                alt={pet.name}
                className="w-full max-h-[85vh] object-contain"
                data-testid="pet-album-lightbox-photo"
              />
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white"
                aria-label="Close"
                data-testid="pet-album-lightbox-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This photo will be removed from {pet.name}&apos;s album. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pet-album-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && handleDeletePhoto(confirmDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="pet-album-delete-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
